import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { useCredit } from "../../context/CreditContext";
import { fmtUsd, TIER_META } from "../../data/casesData";
import { Gift, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const countdown = (readyAt) => {
  if (!readyAt) return "";
  const diff = new Date(readyAt).getTime() - Date.now();
  if (diff <= 0) return "";
  const h = Math.floor(diff / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4), s = Math.floor((diff % 6e4) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function DailyCaseModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const { refreshCredits } = useCredit();
  const [status, setStatus] = useState(null);
  const [won, setWon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [, force] = useState(0);

  const load = async () => {
    if (!user) { setStatus(null); return; }
    try { const r = await axios.get(`${API}/daily/status`, authCfg()); setStatus(r.data); } catch { /* ignore */ }
  };
  useEffect(() => { if (open) { setWon(null); setErr(""); load(); } /* eslint-disable-next-line */ }, [open, user]);
  useEffect(() => { const iv = setInterval(() => force((n) => n + 1), 1000); return () => clearInterval(iv); }, []);

  const claim = async () => {
    if (!user) { onOpenChange(false); window.dispatchEvent(new Event("uc:open-auth")); return; }
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/daily/claim`, {}, authCfg());
      setWon(r.data.item);
      setStatus({ can_claim: false, ready_at: r.data.ready_at });
      refreshCredits();
    } catch (e) { setErr(e.response?.data?.detail || "Could not claim daily case"); }
    finally { setBusy(false); }
  };

  const timeLeft = status && !status.can_claim ? countdown(status.ready_at) : "";
  const meta = won ? (TIER_META[won.tier] || TIER_META.common) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="daily-case-modal" className="max-w-sm rounded-2xl border-[var(--uc-border)] bg-[#15112A] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-extrabold">
            <Gift className="h-5 w-5 text-[var(--uc-green)]" /> Daily Case
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[var(--uc-muted)]">Claim one free case every 24 hours — the reward drops straight into your inventory.</p>

        <div className="relative my-2 flex h-40 items-center justify-center overflow-hidden rounded-2xl border border-[var(--uc-border)] bg-gradient-to-br from-[#241B45] to-[#15112A]">
          <div className="pointer-events-none absolute h-40 w-40 rounded-full bg-[var(--uc-primary)]/25 blur-3xl" />
          {won ? (
            <div className="relative text-center">
              <Sparkles className="mx-auto h-8 w-8" style={{ color: meta.color }} />
              <div className="mt-1 text-sm font-extrabold" style={{ color: meta.color }}>{won.name}</div>
              <div className="text-xs font-bold text-[var(--uc-green)]">{fmtUsd(won.value)}</div>
            </div>
          ) : (
            <Gift className="relative h-16 w-16 text-[#C4B5FD]" />
          )}
        </div>

        {err && <div className="rounded-lg bg-[#3a2230] px-3 py-2 text-xs text-[#FDA4CE]">{err}</div>}

        {status && !status.can_claim && !won ? (
          <button disabled className="w-full rounded-xl bg-white/5 py-3 text-sm font-bold text-[var(--uc-muted)]">
            Next case in {timeLeft || "…"}
          </button>
        ) : won ? (
          <button onClick={() => onOpenChange(false)} data-testid="daily-done-btn" className="w-full rounded-xl border border-white/15 bg-white/5 py-3 text-sm font-bold text-white hover:bg-white/10">
            Nice — {timeLeft ? `next in ${timeLeft}` : "close"}
          </button>
        ) : (
          <button onClick={claim} disabled={busy} data-testid="daily-claim-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] py-3 text-sm font-bold text-white shadow-lg shadow-purple-900/30 hover:from-[#7C3AED] hover:to-[#5B21B6] disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {user ? "Claim daily case" : "Sign in to claim"}
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
